import React, { useCallback, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'APP/Hooks/redux'
import styled from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'

import { Images } from 'APP/Themes'
import PatientActions from 'APP/Redux/PatientRedux'
import I18n from 'APP/I18n'
import { isValidEmailAddress } from 'APP/Lib/Utilities'
import { ResponsiveView } from 'APP/Converse/Layout'
import Button from 'APP/Converse/Button'
import TextInput from 'APP/Converse/Inputs/TextInput'
import LogoHeader from 'APP/Components/LogoHeader'
import ChallengedInvitationFooter from 'APP/Components/ChallengedInvitationFooter'

type InvitationEmailScreenProps = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  'invitationEmail'
>

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  justify-content: space-between;
  flex: 1;
`

const InnerContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const InvitationEmailScreen = ({ route }: InvitationEmailScreenProps) => {
  const { analyticsName } = route?.params || {}

  const dispatch = useDispatch()
  const { requestRunning, challengedInvitationQueue } = useSelector((state) => state.patient)
  const [email, setEmail] = useState('')
  const [showEmailError, setShowEmailError] = useState(false)
  const ref = useRef(null)

  const isInChallengedQueue = (challengedInvitationQueue ?? []).length > 0
  const invitedEmailErrorText = showEmailError ? I18n.t('InvitationEmailScreen.invalidEmail') : ''
  const isFormValid = !requestRunning && isValidEmailAddress(email)

  const handleSubmit = () => {
    setShowEmailError(false)

    if (isFormValid) {
      dispatch(PatientActions.resendInvitationRequest(email))
    }
  }

  const handleBlur = useCallback(() => {
    const isInvalidEmail = !isValidEmailAddress(email)

    if (isInvalidEmail) {
      setShowEmailError(true)
    } else {
      setShowEmailError(false)
    }
  }, [email])

  return (
    <ScreenContainer>
      <ContentContainer>
        <InnerContentContainer>
          <LogoHeader
            source={Images.logoBlue}
            title={I18n.t('InvitationEmailScreen.title')}
            subtitle={I18n.t('InvitationEmailScreen.body')}
          />
          <TextInput
            keyboardType="email-address"
            autoFocus={true}
            ref={ref}
            testID="INVITATION_EMAIL_INPUT"
            onChangeText={setEmail}
            onBlur={handleBlur}
            returnKeyType="next"
            onSubmitEditing={handleSubmit}
            autoCorrect={false}
            label={I18n.t('InvitationEmailScreen.inputLabel')}
            value={email}
            error={invitedEmailErrorText}
          />
        </InnerContentContainer>
        <InnerContentContainer>
          <Button
            widthVariant="full"
            analyticsName="Submit"
            title={I18n.t('InvitationEmailScreen.next')}
            disabled={!isFormValid || requestRunning}
            onPress={handleSubmit}
            isLoading={requestRunning}
            testID="NEXT_BTN"
          />
          {isInChallengedQueue && <ChallengedInvitationFooter analyticsName={analyticsName} />}
        </InnerContentContainer>
      </ContentContainer>
    </ScreenContainer>
  )
}

export default InvitationEmailScreen
